import { Dispatch, SetStateAction } from "react";
import {
  Stack,
  Switch,
  FormControlLabel,
  FormGroup,
  Divider,
  useMediaQuery,
  useTheme
} from "@mui/material";
import { SettingsAccordion } from "./CompanyCard-Setting-Accordion.partial";
import { MetaData } from "../../../Login/typings/login.types";
import LabeledSlider from "../../../../components/Common/LabeledSlider/LabeledSlider";
import { useTranslation } from "react-i18next";
import { displayValueForAllowedNoBookingValue } from "../../functions/company-card.component.functions";
import HomeOfficePolicy from "../../home-office-policy.component";

type BookingSettingsAccordionProps = {
  bookingNormHours: number | undefined;
  maxAllowedNoBookingTime: number;
  newValues: MetaData;
  setNewValues: Dispatch<SetStateAction<MetaData>>;
  homeOffice: { homeOfficeDays: [number, number]; enabled: boolean; mandatory: boolean };
  setHomeOffice: Dispatch<
    SetStateAction<{ homeOfficeDays: [number, number]; enabled: boolean; mandatory: boolean }>
  >;
  workplaceBookingEnabled?: boolean;
};

export const BookingSettingsAccordion = ({
  bookingNormHours,
  maxAllowedNoBookingTime,
  newValues,
  setNewValues,
  homeOffice,
  setHomeOffice,
  workplaceBookingEnabled
}: BookingSettingsAccordionProps) => {
  const { t } = useTranslation();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
  const showWorkplaceSettings = workplaceBookingEnabled ?? true;

  return (
    <SettingsAccordion title={t("_companyCard_generalBookingSettings")} defaultExpanded>
      <Stack spacing={2}>
        <LabeledSlider
          marginRight="5px"
          paddingRight="10px"
          value={newValues.bookingNormHours || bookingNormHours || 8}
          label={t("defaultBookingHours")}
          onChange={(e, v) => {
            /* istanbul ignore next */
            setNewValues(prevState => ({
              ...prevState,
              bookingNormHours: v as number
            }));
          }}
          min={1}
          max={15}
          step={1}
          ariaLabel={"defaultBookingHours"}
          data-testid={"defaultBookingHours"}
        />

        <LabeledSlider
          marginRight="5px"
          paddingRight="10px"
          data-testid={"allowedNoBookingTimeSlider"}
          value={displayValueForAllowedNoBookingValue(
            newValues.maxAllowedNoBookingTime,
            maxAllowedNoBookingTime
          )}
          label={t("AllowedNoBookingTimeLabel")}
          onChange={(e, v) =>
            /* istanbul ignore next */
            setNewValues(prevState => ({
              ...prevState,
              maxAllowedNoBookingTime: v as number
            }))
          }
          min={0}
          max={100}
          step={5}
          ariaLabel={"AllowedNoBookingTimeLabel"}
        />

        <FormGroup sx={[isMobile ? { gap: 1 } : {}]}>
          <FormControlLabel
            control={
              <Switch
                data-testid={"enableAllowBookingCancellations-switch"}
                checked={newValues.allowBookingCancellations}
                onChange={(e, c) => {
                  /* istanbul ignore next */
                  setNewValues(prevState => ({
                    ...prevState,
                    allowBookingCancellations: c
                  }));
                }}
                name="allowBookingCancellations"
                slotProps={{ input: { "aria-label": "secondary checkbox" } }}
              />
            }
            label={t("enableAllowBookingCancellations")}
          />

          <FormControlLabel
            control={
              <Switch
                checked={newValues.bookingsOnSaturdaysAllowed}
                data-testid="enableBookingsOnSaturday-switch"
                onChange={(e, c) => {
                  /* istanbul ignore next */
                  setNewValues(prevState => ({
                    ...prevState,
                    bookingsOnSaturdaysAllowed: c
                  }));
                }}
                name="saturdaybookings"
                slotProps={{ input: { "aria-label": "secondary checkbox" } }}
              />
            }
            label={t("enableBookingsOnSaturday")}
          />

          <FormControlLabel
            control={
              <Switch
                checked={newValues.bookingsOnSundaysAllowed}
                data-testid="enableBookingsOnSunday-switch"
                onChange={(e, c) => {
                  /* istanbul ignore next */
                  setNewValues(prevState => ({
                    ...prevState,
                    bookingsOnSundaysAllowed: c
                  }));
                }}
                name="sundaybookings"
                slotProps={{ input: { "aria-label": "secondary checkbox" } }}
              />
            }
            label={t("enableBookingsOnSunday")}
          />

          <Divider sx={{ width: "100%", margin: "4px 0px 4px 0px", opacity: "0.6" }} />

          <HomeOfficePolicy homeOffice={homeOffice} setHomeOffice={setHomeOffice} />

          {showWorkplaceSettings && (
            <FormControlLabel
              control={
                <Switch
                  checked={newValues.bookingAssistenceActive}
                  onChange={(e, c) => {
                    /* istanbul ignore next */
                    setNewValues(prevState => ({
                      ...prevState,
                      /* istanbul ignore next */
                      bookingAssistenceActive: c
                    }));
                  }}
                />
              }
              label={t("bookingAssistantActive")}
            />
          )}
        </FormGroup>
      </Stack>
    </SettingsAccordion>
  );
};
